/**
 * DCF resubmittals, read-only, for UMED (rulings 2026-10-15): the board Finance works, in UMED's own scope, so the people
 * who prepare and fax the packet (Josie Evans among them, with no Finance role) can see what is with Finance, with
 * UMED, with DCF and declined. GET /umed/resubmittals is the same read as Finance's board.
 *
 * Read-only by construction: no drag, no act, no form. Finance starts and hands over a DCF resubmittal, or UMED starts one
 * from the case's status menu (2026-10-19); UMED works it from the case, so a card opens a docked summary with a link to the UMED case. It reuses Finance's model (cards, chips, facts)
 * and writes nothing.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { OperationalKanban } from '../../components/board/kanban';
import type { KanbanColumnData, TransitionMap } from '../../components/board/kanban';
import '../finance/finance.css';
import { umedResubmittalsApi } from './umedKanbanApi';
import type { ResubmittalBoardPage } from '../finance/resubmittalsApi';
import { ResubmittalCardBody, Facts } from '../finance/ResubmittalsKanban';
import { cardFromRow } from '../finance/resubmittalsModel';
import type { ResubmittalCard, ResubmittalCtx } from '../finance/resubmittalsModel';

// 2026-10-19: UMED starts one too (the case's status menu, REVERSAL / DCF RESUBMITTAL…), handed to UMED at once.
export const UMED_RESUBMITTALS_LINE = 'Finance starts DCF resubmittals here and hands them to UMED; UMED starts one from the case’s status menu (REVERSAL / DCF RESUBMITTAL…). UMED works them from the case.';
export const UMED_RESUBMITTALS_DRAG_REFUSAL = 'This board is read-only here. Record the sending, DCF’s answer or the withdrawal on the case.';
const PAGE_SIZE = 25;

/** Nothing moves and nothing is offered: every drop is refused in one line. */
export const UMED_RESUBMITTAL_TRANSITIONS: TransitionMap<ResubmittalCard, ResubmittalCtx> = {
  moves: {},
  fallbackReason: () => UMED_RESUBMITTALS_DRAG_REFUSAL,
  canDrag: () => false,
};

const READ_ONLY_CTX: ResubmittalCtx = { canManage: false };
type Columns = Array<KanbanColumnData<ResubmittalCard> & { page: number }>;

/** Where a card's case is worked in UMED. */
export const umedCaseHref = (caseId: string) => `/portal/umed?view=work&case=${encodeURIComponent(caseId)}`;

export default function UmedResubmittalsBoard() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [columns, setColumns] = useState<Columns>([]);
  const [head, setHead] = useState<ResubmittalBoardPage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const inFlight = useRef<AbortController | null>(null);
  const known = useRef(new Map<string, ResubmittalCard>());

  const columnOf = (c: ResubmittalBoardPage['columns'][number]) => {
    const cards = c.cards.map(cardFromRow);
    for (const k of cards) known.current.set(k.id, k);
    return { key: c.key, label: c.label, what: c.what, count: c.count, cards, has_more: cards.length < c.count, page: 1, history: c.history, note: c.history ? 'History, last 30 days' : undefined };
  };

  const load = useCallback(async () => {
    if (inFlight.current) inFlight.current.abort();
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    inFlight.current = ctl;
    const stale = () => inFlight.current !== ctl;
    setLoading(true); setErr(null);
    try {
      const r = await umedResubmittalsApi.board({ q: q || undefined, limit: PAGE_SIZE }, ctl ? ctl.signal : undefined);
      if (stale()) return;
      if (!r.success || !r.data) { setErr(r.message || r.error || 'The server refused the board.'); setColumns([]); setHead(null); return; }
      setColumns(r.data.columns.map(columnOf)); setHead(r.data);
    } catch {
      if (!stale()) { setErr('Could not reach the server, so nothing here is current.'); setColumns([]); setHead(null); }
    } finally { if (!stale()) setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (inFlight.current) inFlight.current.abort(); }, []);

  const loadMore = useCallback(async (key: string) => {
    const col = columns.find((c) => c.key === key);
    if (!col || col.loading_more) return;
    setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, loading_more: true } : c)));
    try {
      const r = await umedResubmittalsApi.board({ q: q || undefined, column: key, page: col.page + 1, limit: PAGE_SIZE });
      const got = r.success && r.data ? r.data.columns[0] : null;
      setColumns((cs) => cs.map((c) => {
        if (c.key !== key) return c;
        if (!got) return { ...c, loading_more: false };
        const have = new Set(c.cards.map((k) => k.id));
        const more = got.cards.map(cardFromRow).filter((k) => !have.has(k.id));
        for (const k of more) known.current.set(k.id, k);
        const cards = [...c.cards, ...more];
        return { ...c, loading_more: false, count: got.count, has_more: cards.length < got.count, page: col.page + 1, cards };
      }));
    } catch {
      setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, loading_more: false } : c)));
    }
  }, [columns, q]);

  const nf = new Intl.NumberFormat('en-US');
  const openCount = columns.filter((c) => !c.history).reduce((n, c) => n + c.count, 0);

  return (
    <OperationalKanban<ResubmittalCard, ResubmittalCtx>
      label="Reversals Submitted board"
      idBase="um-rs-kanban"
      unit={['DCF resubmittal', 'DCF resubmittals']}
      columns={columns}
      renderCard={(card) => <ResubmittalCardBody card={card} />}
      cardName={(card) => card.name || 'DCF resubmittal'}
      transitions={UMED_RESUBMITTAL_TRANSITIONS}
      ctx={READ_ONLY_CTX}
      onAct={() => undefined}
      onLoadMore={loadMore}
      loading={loading}
      error={err}
      onRetry={load}
      dragDisabled
      emptyText={q ? `No DCF resubmittal here matches “${q}”.` : 'Nothing here.'}
      toolbar={{
        search: { value: q, onChange: setQ, placeholder: 'Resident or building', label: 'Search Reversals Submitted by resident or building' },
        summary: head ? <>{nf.format(openCount)} open {openCount === 1 ? 'DCF resubmittal' : 'DCF resubmittals'} · {UMED_RESUBMITTALS_LINE}</> : null,
      }}
      detail={{
        openId: open,
        onOpen: setOpen,
        onClose: () => setOpen(null),
        memoryKey: 'umed-resubmittals',
        label: (card) => (card ? `${card.name || 'DCF resubmittal'}: this DCF resubmittal` : 'The open DCF resubmittal'),
        title: (card) => {
          const c = card || (open ? known.current.get(open) : undefined);
          return <strong>{c ? (c.name || 'DCF resubmittal') : 'DCF resubmittal'}</strong>;
        },
        render: (card) => {
          const c = card || (open ? known.current.get(open) : null) || null;
          if (!c) return <p className="um-quiet">This DCF resubmittal is not on the board now.</p>;
          return (
            <div>
              <p className="um-quiet">{UMED_RESUBMITTALS_LINE}</p>
              <Facts card={c} />
              <p><Link className="um-open" to={umedCaseHref(c.case_id)}>Open the UMED case</Link></p>
            </div>
          );
        },
      }}
    />
  );
}
